/* eslint-disable max-lines */
// internal deps
import {
    ALREADY_CLAIMED_ERROR_IMAGE,
    ALREADY_CLAIMED_STATUS_CODE,
    CardBannerConfig,
    DefaultCardConfig,
    EXPIRED_GIFTCARD_ERROR_IMAGE,
    EXPIRED_GIFTCARD_STATUS_CODE,
    INVALID_GIFTCARD_ERROR_IMAGE,
} from "constants/giftcard";
import { isEmpty } from "@swiggy-private/js-utils";
import Blaze from "@swiggy-private/blaze.js";
import { SwgyHeaderKeysList } from "@swiggy-private/checkout-types";
import type { PaymentMainProps } from "@swiggy-private/payment-client";
import type { ISwiggyHeaders } from "@swiggy-private/swiggy-money-context/src/interfaces";

//constants
import { ORIGINAL_BUSINESS_LINE, SWIGGY_MONEY } from "../constants";
import { sendErrorBoundaryEvent } from "./newrelic";

export const isBrowserEnv = () => {
    return typeof window !== "undefined";
};

export const getUserAgent = (): string => {
    return isBrowserEnv() && window.navigator && window.navigator.userAgent ? window.navigator.userAgent : "";
};

export const getAppHost = (): string => {
    return isBrowserEnv() && window.location && window.location.hostname ? window.location.hostname : "";
};

export function isApp() {
    return window.AndroidBridge || window.webkit?.messageHandlers?.nativeRequest;
}

export function isIOS() {
    if (typeof window !== "undefined") {
        return !!window.webkit?.messageHandlers?.nativeRequest;
    }
}

export function isAndroid() {
    if (typeof window !== "undefined") {
        return !!window.AndroidBridge;
    }
}

export const allowMetricCapture = () => {
    return (
        process.env.NODE_ENV === "production" &&
        isBrowserEnv() &&
        (window.location.hostname.indexOf("swiggy.com") > 0 ||
            window.location.hostname.indexOf("singapore.swig.gy") > 0)
    );
};

export const filterHeadersFromSwiggyHeaders = (swiggyHeaders: Record<string, string | undefined>) => {
    return {
        deviceId: swiggyHeaders?.deviceid || "--",
        sid: swiggyHeaders?.sid || "--",
        userId: swiggyHeaders?.userid || "--",
        platform: swiggyHeaders?.platform || "--",
        appVersionCode: swiggyHeaders?.["version-code"] || "--",
        xChannel: swiggyHeaders?.["x-channel"] || "--",
    };
};

export const sendCustomNREvent = (
    type: string,
    attributes: Record<string, string | number> = {},
    headers: ISwiggyHeaders = {},
) => {
    const swgyHeaders = headers ? filterHeadersFromSwiggyHeaders(headers) : {};
    const evBody = attributes ?? {};
    Blaze.recordCustomEvent({
        eventType: type,
        ...evBody,
        ...swgyHeaders,
    });
};

export const getPaymentHeaders = (swiggyHeaders: ISwiggyHeaders): PaymentMainProps["headers"] => {
    let paymentAmount = "";
    try {
        // setting payment amount from auth_headers if already present
        // this is done to handle payment failed cases where we need to pass payment amount
        // as header to load payment page directly i.e. bypassing add money screen
        const authHeaders = localStorage.getItem("auth_headers");
        if (authHeaders) {
            const headers = JSON.parse(authHeaders);
            paymentAmount = headers?.paymentamount;
        }
    } catch (e) {
        sendErrorBoundaryEvent(e as Error, undefined, swiggyHeaders);
    }

    // removing unused headers as invalid values of these headers can cause issues in payments sdk
    // long term fix is to add a check in payment sdk to filter required headers
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { maintext, address, ...restHeaders } = swiggyHeaders;

    return {
        ...restHeaders,
        [SwgyHeaderKeysList.marketplaceBusinessLine.toLowerCase()]: SWIGGY_MONEY,
        [SwgyHeaderKeysList.marketplaceCategory.toLowerCase()]: SWIGGY_MONEY,
        [SwgyHeaderKeysList.paymentamount.toLowerCase()]: paymentAmount,
        [SwgyHeaderKeysList.paymentSecretKey.toLowerCase()]: process.env.NEXT_PUBLIC_PAYMENT_SECRET_KEY ?? "",
    } as PaymentMainProps["headers"];
};

export const setOriginalBusinessLine = () => {
    localStorage.setItem(ORIGINAL_BUSINESS_LINE, `"${SWIGGY_MONEY}"`);
};

export const getAuthHeaders = () => {
    const authHeaders = localStorage.getItem("auth_headers") || "{}";
    try {
        return JSON.parse(authHeaders);
    } catch (_e) {
        return {};
    }
};

export const overrideBrowserBack = (override: boolean, cb: VoidFunction) => {
    if (typeof window !== "undefined") {
        if (override) {
            window.onpopstate = cb;
            return;
        }
        window.onpopstate = () => true;
    }
};

export const getGiftcardDetails = () => {
    let sessionData;

    try {
        sessionData = JSON.parse(sessionStorage.getItem("gift-details") as string);
    } catch (_e) {
        void 0;
    }

    if (!sessionData) {
        return null;
    }

    return {
        cardAmount: sessionData.originalAmount,
        message: sessionData.receiverMeta.message,
        amountPaid: sessionData.discountedAmount,
        paymentMode: sessionData.paymentMode,
        orderId: sessionData.orderId,
        dateOfPurchase: sessionData.dateOfPurchase,
        shareLink: sessionData.shareLink,
    };
};

export const getPaymentHeadersForGiftcard = (swiggyHeaders: ISwiggyHeaders): PaymentMainProps["headers"] => {
    let paymentAmount = "";

    try {
        paymentAmount = JSON.parse(sessionStorage.getItem("gift-details") as string)?.discountedAmount;
    } catch (e) {
        sendErrorBoundaryEvent(e as Error, undefined, swiggyHeaders);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { maintext, address, ...restHeaders } = swiggyHeaders;

    return {
        ...restHeaders,
        [SwgyHeaderKeysList.marketplaceBusinessLine.toLowerCase()]: SWIGGY_MONEY,
        [SwgyHeaderKeysList.marketplaceCategory.toLowerCase()]: SWIGGY_MONEY,
        [SwgyHeaderKeysList.paymentamount.toLowerCase()]: paymentAmount,
        [SwgyHeaderKeysList.paymentSecretKey.toLowerCase()]: process.env.NEXT_PUBLIC_PAYMENT_SECRET_KEY ?? "",
    } as PaymentMainProps["headers"];
};

export const getCurrentGiftingCardConfig = () => {
    const currentTime = Date.now(); // UTC time

    const cardConfig = CardBannerConfig.find(
        (config) => config.timeRangeStart < currentTime && config.timeRangeEnd >= currentTime,
    );
    if (isEmpty(cardConfig)) return DefaultCardConfig;
    return cardConfig;
};

export const getGiftcardClaimingErrorImage = (statusCode: number): string => {
    if (statusCode == ALREADY_CLAIMED_STATUS_CODE) {
        return ALREADY_CLAIMED_ERROR_IMAGE;
    }
    if (statusCode == EXPIRED_GIFTCARD_STATUS_CODE) {
        return EXPIRED_GIFTCARD_ERROR_IMAGE;
    }
    return INVALID_GIFTCARD_ERROR_IMAGE;
};

export const isGiftCardFlow = (): boolean => {
    const giftDetails = sessionStorage && sessionStorage.getItem("gift-details");
    return !isEmpty(giftDetails);
};

export const isPreviousRouteExistsInStack = (): boolean => {
    return isBrowserEnv() && window.history.state && window.history.state.idx > 0;
};
